import { useMemo } from 'react';
import { useDropsControllerGetSortedDrops, useDropsControllerGetAllParticipations } from '~/lib/api';

import type { ParticipationStatusDto, SortedDropItemResponseDto } from '~/lib/api';

interface DropWithParticipation {
  drop: SortedDropItemResponseDto;
  participation: ParticipationStatusDto | undefined;
}

const COMPLETED_UNACKNOWLEDGED_PRIORITY = 1;
const ACTIVE_WITH_PARTICIPATION_PRIORITY = 2;
const ACTIVE_WITHOUT_PARTICIPATION_PRIORITY = 3;
const UPCOMING_PRIORITY = 4;
const OTHER_PRIORITY = 5;

const getClientSidePriority = (item: DropWithParticipation): number => {
  const { drop, participation } = item;
  const now = new Date();
  const dropStartTime = new Date(drop.startTime);

  const isUpcoming = dropStartTime > now;
  const isActive = drop.status === 'active' && dropStartTime <= now;
  const isCompleted = drop.status === 'completed';
  const hasParticipation = !!participation;
  const isAcknowledged = participation?.acknowledged ?? false;

  // Completed drops with unacknowledged participation - highest priority
  if (isCompleted && hasParticipation && !isAcknowledged) {
    return COMPLETED_UNACKNOWLEDGED_PRIORITY;
  }

  // Active drops where user has participated
  if (isActive && hasParticipation) {
    return ACTIVE_WITH_PARTICIPATION_PRIORITY;
  }

  // Active drops where user has not participated
  if (isActive && !hasParticipation) {
    return ACTIVE_WITHOUT_PARTICIPATION_PRIORITY;
  }

  // Upcoming drops (not started yet)
  if (isUpcoming) {
    return UPCOMING_PRIORITY;
  }

  // Everything else
  return OTHER_PRIORITY;
};

const shouldShowDrop = (item: DropWithParticipation): boolean => {
  const { drop, participation } = item;
  const hasParticipation = !!participation;
  const isAcknowledged = participation?.acknowledged ?? false;
  const isCompleted = drop.status === 'completed';

  // Hide completed drops without participation
  if (isCompleted && !hasParticipation) {
    return false;
  }

  // Hide completed drops with acknowledged participation
  if (isCompleted && hasParticipation && isAcknowledged) {
    return false;
  }

  return true;
};

export const useHomeDrops = () => {
  const { data: sortedDrops, isLoading: isSortedDropsLoading } = useDropsControllerGetSortedDrops();

  const { data: userParticipation } = useDropsControllerGetAllParticipations();

  const shownDrops = useMemo(() => {
    if (!sortedDrops?.data) return [];

    // Combine drops with participation data
    const dropsWithParticipation: DropWithParticipation[] = sortedDrops.data.map((drop) => ({
      drop,
      participation: userParticipation?.data.find((participation) => participation.dropId === drop.id),
    }));

    // Filter and sort based on user participation
    return dropsWithParticipation.filter(shouldShowDrop).sort((a, b) => {
      const priorityA = getClientSidePriority(a);
      const priorityB = getClientSidePriority(b);

      // Sort by priority first
      if (priorityA !== priorityB) {
        return priorityA - priorityB;
      }

      // Within same priority, sort by start time (upcoming) or end time (others)
      const now = new Date();
      const isUpcomingA = new Date(a.drop.startTime) > now;
      const isUpcomingB = new Date(b.drop.startTime) > now;

      const dateA = isUpcomingA ? new Date(a.drop.startTime) : new Date(a.drop.endTime);
      const dateB = isUpcomingB ? new Date(b.drop.startTime) : new Date(b.drop.endTime);

      return dateA.getTime() - dateB.getTime();
    });
  }, [sortedDrops?.data, userParticipation?.data]);
  return {
    shownDrops,
    userParticipation,
    isLoading: isSortedDropsLoading,
  };
};
